// A zone row's games: a catalogue row by id (accounts resolved it), hand picks, or a rule with rows.md's field names
// evaluated here. Pure; the launcher ports the same logic (launcher-zones).

import type { Catalogue, Game } from "../catalogue.ts";
import type { Rule, ZoneRow } from "./spec.ts";

const DEFAULT_LIMIT = 30;
const DEFAULT_MIN = 4;

/** A small deterministic hash (FNV-1a) for the daily shuffle. */
function hash(s: string): number {
  let x = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) x = Math.imul(x ^ s.charCodeAt(i), 0x01000193) >>> 0;
  return x;
}

const anyOf = (want: string[] | undefined, have: (string | null | undefined)[]): boolean => !want?.length || want.some((w) => have.includes(w));

function facetOk(g: Game, f: string): boolean {
  switch (f) {
    case "multiplayer":
      return g.facets.multiplayer;
    case "no_preplay":
      return g.facets.prePlay.length === 0;
    case "staff_pick":
      return g.staffPick === true;
    default:
      return (g.facets.input as string[]).includes(f);
  }
}

/** Does a game match a rule? Filters AND together; inside one filter, values are "any of". Coming soon never matches. */
export function matchesRule(g: Game, r: Rule): boolean {
  if (g.status === "coming_soon") return false;
  if (r.kind && g.kind !== r.kind) return false;
  if (!anyOf(r.genre, [g.genre])) return false;
  if (!anyOf(r.theme, g.themes ?? [])) return false;
  if (!anyOf(r.mood, g.mood ?? [])) return false;
  if (!anyOf(r.style, [g.style])) return false;
  if (!anyOf(r.session, [g.session])) return false;
  return (r.facet ?? []).every((f) => facetOk(g, f));
}

/** Order matches by the rule's sort. `art_first` keeps games with art (or a cover) ahead, each half in daily order. */
export function sortGames(games: Game[], r: Rule, seed: string, now: number): Game[] {
  const day = new Date(now).toISOString().slice(0, 10);
  const daily = (a: Game, b: Game) => hash(`${day}:${seed}:${a.id}`) - hash(`${day}:${seed}:${b.id}`);
  const out = [...games];
  switch (r.sort) {
    case "listed_at":
      return out.sort((a, b) => (b.listedAt ?? 0) - (a.listedAt ?? 0));
    case "updated_at":
      return out.sort((a, b) => (b.versionUpdatedAt ?? 0) - (a.versionUpdatedAt ?? 0));
    case "art_first": {
      // real art, then a typographic cover, then none (accounts' order; `artKind` is catalogue wave 12)
      const art = (g: Game) => {
        const k = (g as Game & { artKind?: string | null }).artKind;
        return k === "real" ? 0 : k === "type" ? 1 : k === undefined && (g.art || g.cover) ? 0 : 2;
      };
      return out.sort((a, b) => art(a) - art(b) || daily(a, b));
    }
    case "editorial":
      return [];
    default:
      return out.sort(daily);
  }
}

/**
 * The games for one zone row, or [] when it has fewer than `min` (then the row is hidden). Pins (`games`) come first,
 * then the catalogue row's games or the rule's matches, without repeats and without the row's `exclude` ids (taken
 * out before `limit` and `min`, so an excluded game never costs the row a place).
 */
export function zoneRowGames(cat: Catalogue, row: ZoneRow, seed: string, now = Date.now()): Game[] {
  const byId = new Map(cat.games.map((g) => [g.id, g]));
  const pins = (row.games ?? []).map((id) => byId.get(id)).filter((g): g is Game => !!g);
  let rest: Game[] = [];
  if (row.row) {
    const r = cat.rows?.find((x) => x.id === row.row) ?? cat.lists.find((x) => x.id === row.row);
    rest = (r?.games ?? []).map((id) => byId.get(id)).filter((g): g is Game => !!g);
    if (row.rule?.sort) rest = sortGames(rest, row.rule, seed, now);
  } else if (row.rule) {
    const rule = row.rule;
    rest = sortGames(cat.games.filter((g) => matchesRule(g, rule)), rule, seed, now);
  }
  if (row.exclude?.length) {
    const out = new Set(row.exclude);
    rest = rest.filter((g) => !out.has(g.id));
  }
  const seen = new Set<string>();
  const all = [...pins, ...rest].filter((g) => !seen.has(g.id) && (seen.add(g.id), true));
  const limit = Math.min(60, row.rule?.limit ?? DEFAULT_LIMIT);
  const min = Math.min(limit, row.rule?.min ?? (row.row || row.games ? 1 : DEFAULT_MIN));
  const out = all.slice(0, limit);
  return out.length >= min ? out : [];
}
